import React, { useMemo } from 'react';
import { ShieldCheckIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { PERMISSOES, PERMISSOES_GRUPOS, isPermissaoDisponivel } from '../../types/usuarios';
import { usePapeis } from '../../hooks/usePapeis';

interface Props {
  /** Papel do usuário. `null`/undefined = sem papel ⇒ sem acesso. */
  papelId?: number | null;
  /** Perfil (tipo de conta). ADMIN bypassa tudo via Role — o papel é inócuo pra ele. */
  perfil?: string;
}

/**
 * Permissões do usuário — <b>somente leitura</b> (RBAC puro, 2026-07-19).
 *
 * Antes isto editava permissões avulsas por usuário. Elas deixaram de existir: o
 * acesso vem do <b>papel</b>, e é lá que se muda — o que faz a mudança valer pra
 * todos daquele cargo de uma vez, em vez de usuário a usuário. Aqui a gente só
 * mostra o que o papel concede, pra o admin não procurar checkbox que não existe mais.
 */
const PermissoesEditor: React.FC<Props> = ({ papelId, perfil }) => {
  const { data: papeis, isLoading } = usePapeis();

  const papel = useMemo(
    () => (papelId ? (papeis ?? []).find(p => p.id === papelId) : undefined),
    [papeis, papelId]
  );

  const concedidas = useMemo(() => new Set(papel?.permissoes ?? []), [papel]);

  const grupos = useMemo(
    () =>
      PERMISSOES_GRUPOS
        .map(g => ({ ...g, chaves: g.chaves.filter(isPermissaoDisponivel) }))
        .filter(g => g.chaves.length > 0),
    []
  );

  if (perfil === 'ADMIN') {
    return (
      <div className="flex items-start gap-2.5 rounded-iris border border-ok-border bg-ok-soft px-3 py-2.5">
        <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
        <p className="text-[13px] text-tx">
          <strong>Administrador do sistema</strong> — acesso total a todas as telas,
          independente de papel. Reservado ao suporte.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <p className="py-6 text-sm text-tx-3">Carregando permissões…</p>;
  }

  if (!papel) {
    return (
      <div className="flex items-start gap-2.5 rounded-iris border border-warning-border bg-warning-soft px-3 py-2.5">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <p className="text-[13px] text-tx">
          <strong>Sem papel — sem acesso.</strong> Este usuário loga mas não enxerga nenhuma
          tela. Escolha um papel na aba <strong>Dados</strong> para liberar o acesso.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-iris border border-accent-ring bg-accent-soft px-3 py-2.5">
        <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-text" />
        <p className="text-[13px] text-tx">
          O acesso vem do papel <strong>{papel.nome}</strong>
          {papel.descricao ? ` — ${papel.descricao}` : ''}. Para mudar o que este cargo enxerga,
          edite o papel na aba <strong>Papéis</strong> — a alteração vale para os{' '}
          <strong>{papel.qtdeUsuarios}</strong> usuário(s) que o usam. Para dar outro acesso só a
          esta pessoa, mova-a para outro papel na aba <strong>Dados</strong>.
        </p>
      </div>

      <div className="overflow-hidden rounded-iris-lg border border-line">
        {grupos.map(grupo => (
          <div key={grupo.label} className="border-b border-line last:border-b-0">
            <div className="bg-surface-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-tx-2">
              {grupo.label}
            </div>
            <ul className="divide-y divide-line">
              {grupo.chaves.map(chave => {
                const tem = concedidas.has(chave);
                return (
                  <li key={chave} className="flex items-center gap-2.5 px-3 py-1.5">
                    <span
                      className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-iris-sm border ${
                        tem ? 'border-accent-ring bg-accent-soft' : 'border-line bg-surface-2'
                      }`}
                    >
                      {tem && <CheckIcon className="h-2.5 w-2.5 text-accent-text" strokeWidth={3} />}
                    </span>
                    <span className={`text-[13px] ${tem ? 'text-tx' : 'text-tx-3'}`}>
                      {PERMISSOES[chave].label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PermissoesEditor;
